import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Lock, LogOut, RefreshCw, Download, Search, Trash2, Eye, Users, Globe } from "lucide-react";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

const fmtMoney = (n) => "$" + Math.round(n).toLocaleString("en-US");

const STATUS_STYLES = {
  new: "bg-nexcyan/10 text-nexcyan border-nexcyan/30",
  contacted: "bg-nexorange/10 text-nexorange border-nexorange/30",
  closed: "bg-emerald-100 text-emerald-700 border-emerald-300",
};

function NotesCell({ lead, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(lead.notes || "");

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        data-testid={`lead-notes-view-${lead.id}`}
        className={`text-left text-xs leading-relaxed ${lead.notes ? "text-nexink/70" : "italic text-nexink/35 hover:text-nexcyan"}`}
      >
        {lead.notes || "Add note..."}
      </button>
    );
  }
  return (
    <textarea
      autoFocus
      rows={3}
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => {
        setEditing(false);
        if (val !== (lead.notes || "")) onSave(lead.id, val);
      }}
      data-testid={`lead-notes-input-${lead.id}`}
      className="w-full min-w-[180px] rounded-lg border border-nexcyan/50 bg-white p-2 text-xs text-nexink outline-none"
      placeholder="Call outcome, next steps..."
    />
  );
}

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

function downloadCSV(headers, rows, filename) {
  const csv = [headers, ...rows].map((r) => r.map(csvCell).join(",")).join("\r\n");
  const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("nexorbit_admin_token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const logout = useCallback(() => {
    sessionStorage.removeItem("nexorbit_admin_token");
    setToken("");
    setAuthed(false);
    setLeads([]);
    setEstimates([]);
  }, []);

  const load = useCallback(
    async (t) => {
      setLoading(true);
      try {
        const h = { headers: { Authorization: `Bearer ${t}` } };
        const [l, e, a] = await Promise.all([
          axios.get(`${API}/leads`, h),
          axios.get(`${API}/estimates`, h),
          axios.get(`${API}/admin/analytics`, h),
        ]);
        setLeads(l.data);
        setEstimates(e.data);
        setAnalytics(a.data);
        setAuthed(true);
        setError("");
      } catch {
        logout();
        setError("Session expired — please sign in again.");
      } finally {
        setLoading(false);
      }
    },
    [logout]
  );

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const patchLead = async (id, payload, optimistic) => {
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, ...optimistic } : l)));
    try {
      await axios.patch(`${API}/leads/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      load(token);
    }
  };

  const setLeadStatus = (id, status) => patchLead(id, { status }, { status });
  const saveLeadNotes = (id, notes) => patchLead(id, { notes }, { notes });

  const deleteItem = async (kind, id) => {
    if (!window.confirm("Delete this entry permanently?")) return;
    try {
      await axios.delete(`${API}/${kind}/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (kind === "leads") setLeads((ls) => ls.filter((l) => l.id !== id));
      else setEstimates((es) => es.filter((e) => e.id !== id));
    } catch {
      load(token);
    }
  };

  const [convertErrors, setConvertErrors] = useState({});
  const convertEstimate = async (id) => {
    setConvertErrors((m) => ({ ...m, [id]: undefined }));
    try {
      await axios.post(`${API}/estimates/${id}/convert`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      load(token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setConvertErrors((m) => ({ ...m, [id]: typeof d === "string" ? d : "Could not convert" }));
    }
  };

  const q = search.trim().toLowerCase();
  const filteredLeads = leads.filter(
    (l) =>
      (statusFilter === "all" || (l.status || "new") === statusFilter) &&
      (!q ||
        [l.full_name, l.email, l.phone, l.company, l.message, l.notes, (l.services || []).join(" ")]
          .join(" ")
          .toLowerCase()
          .includes(q))
  );

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("nexorbit_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Sign-in failed. Try again.");
      setLoading(false);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-nexblue flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-[0_20px_60px_rgba(0,0,0,0.3)]">
          <div className="flex justify-center"><Logo /></div>
          <h1 className="mt-6 text-center font-display text-xl font-bold text-nexblue">Admin Access</h1>
          <p className="mt-1 text-center text-sm text-nexink/60">Sign in to view enquiries and estimates</p>
          <form onSubmit={login} className="mt-6">
            <div className="relative">
              <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-nexink/40" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Admin password"
                data-testid="admin-password-input"
                className="h-12 w-full rounded-xl border border-nexblue/15 pl-11 pr-4 text-sm text-nexink outline-none focus:border-nexcyan"
              />
            </div>
            {error && (
              <p className="mt-3 text-xs font-medium text-red-500" data-testid="admin-login-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-btn"
              className="mt-4 w-full rounded-full bg-nexorange py-3 text-sm font-bold text-nexblue transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
          <a href="/" className="mt-5 block text-center text-xs font-semibold text-nexink/50 hover:text-nexcyan" data-testid="admin-back-home-link">
            ← Back to website
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-nexlight" data-testid="admin-dashboard">
      <header className="bg-nexblue text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <button
              onClick={() => load(token)}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs font-semibold text-white/80 transition-colors hover:border-nexcyan hover:text-nexcyan"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs font-semibold text-white/80 transition-colors hover:bg-white/20"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10 space-y-12">
        {analytics && (
          <section data-testid="admin-traffic-section">
            <h2 className="font-display text-xl font-bold text-nexblue mb-4">Website Traffic</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              {[
                { icon: Eye, label: "Hits Today", value: analytics.today, testId: "traffic-today" },
                { icon: Users, label: "Unique Visitors Today", value: analytics.unique_today, testId: "traffic-unique-today" },
                { icon: Globe, label: "Total Hits (All Time)", value: analytics.total, testId: "traffic-total" },
              ].map(({ icon: Icon, label, value, testId }) => (
                <div key={label} className="rounded-2xl border border-nexblue/10 bg-white p-5 flex items-center gap-4" data-testid={testId}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-nexblue text-nexcyan"><Icon size={20} /></span>
                  <span>
                    <span className="block font-display text-2xl font-bold text-nexblue tabular-nums">{value}</span>
                    <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-nexink/50">{label}</span>
                  </span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 rounded-2xl border border-nexblue/10 bg-white p-6" data-testid="traffic-chart">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-nexink/50 mb-5">Last 14 Days</p>
                <div className="flex items-end gap-1.5 h-36">
                  {analytics.series.map((d) => {
                    const max = Math.max(...analytics.series.map((x) => x.hits), 1);
                    return (
                      <div key={d.day} className="group relative flex-1 flex flex-col items-center justify-end h-full">
                        <span className="absolute -top-1 hidden group-hover:block rounded bg-nexblue px-2 py-1 text-[10px] font-semibold text-white whitespace-nowrap z-10">
                          {d.day.slice(5)}: {d.hits} hits · {d.unique} unique
                        </span>
                        <div
                          className="w-full rounded-t-md bg-nexcyan/70 transition-colors group-hover:bg-nexorange"
                          style={{ height: `${Math.max((d.hits / max) * 100, 3)}%` }}
                          data-testid={`traffic-bar-${d.day}`}
                        />
                        <span className="mt-2 text-[9px] text-nexink/40">{d.day.slice(8)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="rounded-2xl border border-nexblue/10 bg-white p-6" data-testid="traffic-top-pages">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-nexink/50 mb-4">Top Pages</p>
                <ul className="space-y-3">
                  {analytics.top_pages.length === 0 && <li className="text-sm text-nexink/40">No traffic yet</li>}
                  {analytics.top_pages.map((p) => (
                    <li key={p.path} className="flex items-center justify-between text-sm">
                      <span className="font-medium text-nexink/80">{p.path === "/" ? "/ (home)" : p.path}</span>
                      <span className="rounded-full bg-nexcyan/10 px-2.5 py-0.5 text-xs font-bold text-nexcyan">{p.hits}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {[
            { label: "Total Enquiries", value: leads.length, cls: "bg-nexblue text-nexcyan", testId: "admin-leads-count" },
            { label: "New", value: leads.filter((l) => (l.status || "new") === "new").length, cls: "bg-nexcyan/10 text-nexcyan", testId: "admin-new-count" },
            { label: "Contacted", value: leads.filter((l) => l.status === "contacted").length, cls: "bg-nexorange/10 text-nexorange", testId: "admin-contacted-count" },
            { label: "Closed", value: leads.filter((l) => l.status === "closed").length, cls: "bg-emerald-100 text-emerald-600", testId: "admin-closed-count" },
            { label: "Estimates", value: estimates.length, cls: "bg-nexblue/5 text-nexblue", testId: "admin-estimates-count" },
          ].map(({ label, value, cls, testId }) => (
            <div key={label} className="rounded-2xl border border-nexblue/10 bg-white p-5" data-testid={testId}>
              <span className={`inline-flex h-9 min-w-9 items-center justify-center rounded-xl px-2 font-display text-lg font-bold ${cls}`}>
                {value}
              </span>
              <span className="mt-2 block text-[11px] font-semibold uppercase tracking-[0.15em] text-nexink/50">{label}</span>
            </div>
          ))}
        </div>

        <section>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <h2 className="font-display text-xl font-bold text-nexblue">Consultation Enquiries</h2>
            <button
              onClick={() =>
                downloadCSV(
                  ["Date", "Status", "Name", "Email", "Phone", "Company", "Company Size", "Services", "Message", "Notes"],
                  filteredLeads.map((l) => [fmtDate(l.created_at), l.status || "new", l.full_name, l.email, l.phone, l.company, l.company_size, (l.services || []).join("; "), l.message, l.notes || ""]),
                  "nexorbit-enquiries.csv"
                )
              }
              disabled={filteredLeads.length === 0}
              data-testid="admin-export-leads-btn"
              className="inline-flex items-center gap-2 rounded-full bg-nexblue px-4 py-2 text-xs font-semibold text-white transition-transform duration-300 hover:scale-[1.04] disabled:opacity-40"
            >
              <Download size={14} /> Download CSV
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex rounded-full border border-nexblue/10 bg-white p-1" data-testid="admin-status-tabs">
              {[
                ["all", "All"],
                ["new", "New"],
                ["contacted", "Contacted"],
                ["closed", "Closed"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setStatusFilter(key)}
                  data-testid={`admin-filter-${key}`}
                  className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors duration-200 ${
                    statusFilter === key ? "bg-nexblue text-white" : "text-nexink/60 hover:text-nexblue"
                  }`}
                >
                  {label}
                  <span className="ml-1.5 opacity-60">
                    {key === "all" ? leads.length : leads.filter((l) => (l.status || "new") === key).length}
                  </span>
                </button>
              ))}
            </div>
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-nexink/40" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, company, message..."
                data-testid="admin-search-input"
                className="h-10 w-full rounded-full border border-nexblue/10 bg-white pl-10 pr-4 text-sm text-nexink outline-none transition-colors focus:border-nexcyan"
              />
            </div>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-nexblue/10 bg-white" data-testid="admin-leads-table">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b border-nexblue/10 text-left text-xs uppercase tracking-[0.12em] text-nexink/50">
                  <th className="px-5 py-4">Date</th><th className="px-5 py-4">Status</th><th className="px-5 py-4">Name</th><th className="px-5 py-4">Contact</th>
                  <th className="px-5 py-4">Company</th><th className="px-5 py-4">Services</th><th className="px-5 py-4">Message</th><th className="px-5 py-4">Notes</th><th className="px-5 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.length === 0 && (
                  <tr><td colSpan={9} className="px-5 py-10 text-center text-nexink/40" data-testid="admin-no-results">
                    {leads.length === 0 ? "No enquiries yet" : "No enquiries match your filters"}
                  </td></tr>
                )}
                {filteredLeads.map((l) => (
                  <tr key={l.id} className="border-b border-nexblue/5 last:border-0 align-top hover:bg-nexlight/60">
                    <td className="px-5 py-4 whitespace-nowrap text-nexink/60">{fmtDate(l.created_at)}</td>
                    <td className="px-5 py-4">
                      <select
                        value={l.status || "new"}
                        onChange={(e) => setLeadStatus(l.id, e.target.value)}
                        data-testid={`lead-status-select-${l.id}`}
                        className={`cursor-pointer rounded-full border px-3 py-1.5 text-xs font-bold capitalize outline-none transition-colors ${STATUS_STYLES[l.status || "new"]}`}
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="closed">Closed</option>
                      </select>
                    </td>
                    <td className="px-5 py-4 font-semibold text-nexblue">{l.full_name}</td>
                    <td className="px-5 py-4 text-nexink/70">{l.email}<br /><span className="text-nexink/50">{l.phone}</span></td>
                    <td className="px-5 py-4 text-nexink/70">{l.company}<br /><span className="text-nexink/50">{l.company_size}</span></td>
                    <td className="px-5 py-4 text-nexink/70">{l.services?.join(", ") || "—"}</td>
                    <td className="px-5 py-4 text-nexink/70 max-w-[260px]">{l.message}</td>
                    <td className="px-5 py-4 max-w-[220px]"><NotesCell lead={l} onSave={saveLeadNotes} /></td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() => deleteItem("leads", l.id)}
                        data-testid={`admin-delete-lead-${l.id}`}
                        aria-label="Delete enquiry"
                        className="text-nexink/30 transition-colors hover:text-red-500"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold text-nexblue">Calculator Estimates</h2>
            <button
              onClick={() =>
                downloadCSV(
                  ["Date", "Email", "Monthly Spend", "Workloads", "Est Savings/mo", "Est Savings/yr", "Ops Hours/mo"],
                  estimates.map((e) => [fmtDate(e.created_at), e.email, e.monthly_spend, e.workloads, e.monthly_savings, e.annual_savings, e.hours_saved]),
                  "nexorbit-estimates.csv"
                )
              }
              disabled={estimates.length === 0}
              data-testid="admin-export-estimates-btn"
              className="inline-flex items-center gap-2 rounded-full bg-nexblue px-4 py-2 text-xs font-semibold text-white transition-transform duration-300 hover:scale-[1.04] disabled:opacity-40"
            >
              <Download size={14} /> Download CSV
            </button>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-nexblue/10 bg-white" data-testid="admin-estimates-table">
            <table className="w-full min-w-[700px] text-sm">
              <thead>
                <tr className="border-b border-nexblue/10 text-left text-xs uppercase tracking-[0.12em] text-nexink/50">
                  <th className="px-5 py-4">Date</th><th className="px-5 py-4">Email</th><th className="px-5 py-4">Monthly Spend</th>
                  <th className="px-5 py-4">Workloads</th><th className="px-5 py-4">Est. Savings /mo</th><th className="px-5 py-4">Est. Savings /yr</th><th className="px-5 py-4">Pipeline</th><th className="px-5 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {estimates.length === 0 && (
                  <tr><td colSpan={8} className="px-5 py-10 text-center text-nexink/40">No estimates captured yet</td></tr>
                )}
                {estimates.map((e) => (
                  <tr key={e.id} className="border-b border-nexblue/5 last:border-0 hover:bg-nexlight/60">
                    <td className="px-5 py-4 whitespace-nowrap text-nexink/60">{fmtDate(e.created_at)}</td>
                    <td className="px-5 py-4 font-semibold text-nexblue">{e.email}</td>
                    <td className="px-5 py-4 text-nexink/70">{fmtMoney(e.monthly_spend)}</td>
                    <td className="px-5 py-4 text-nexink/70">{e.workloads}</td>
                    <td className="px-5 py-4 text-nexink/70">{fmtMoney(e.monthly_savings)}</td>
                    <td className="px-5 py-4 font-semibold text-nexorange">{fmtMoney(e.annual_savings)}</td>
                    <td className="px-5 py-4">
                      {e.converted ? (
                        <span className="inline-flex items-center rounded-full border border-emerald-300 bg-emerald-100 px-3 py-1.5 text-xs font-bold text-emerald-700" data-testid={`estimate-converted-badge-${e.id}`}>
                          In Pipeline
                        </span>
                      ) : (
                        <>
                          <button
                            onClick={() => convertEstimate(e.id)}
                            data-testid={`estimate-convert-btn-${e.id}`}
                            className="inline-flex items-center rounded-full bg-nexblue px-3.5 py-1.5 text-xs font-bold text-white transition-transform duration-300 hover:scale-[1.05]"
                          >
                            Convert to Enquiry
                          </button>
                          {convertErrors[e.id] && (
                            <p className="mt-1.5 max-w-[160px] text-xs text-red-500" data-testid={`estimate-convert-error-${e.id}`}>{convertErrors[e.id]}</p>
                          )}
                        </>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() => deleteItem("estimates", e.id)}
                        data-testid={`admin-delete-estimate-${e.id}`}
                        aria-label="Delete estimate"
                        className="text-nexink/30 transition-colors hover:text-red-500"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
